import { getApiUrl } from "../utils/general.js";
import axios from "axios";

/**
 * Service per interagire con i report Power BI e gestire l'embedding in modalità App-Owns-Data
 */
const PowerBIService = {
  /**
   * Ottiene le informazioni necessarie per incorporare il report Power BI in modalità App-Owns-Data
   * Utilizza il nuovo endpoint che ottiene l'embedUrl corretto dalle API di Power BI
   * @param {string} reportId - ID del report
   * @param {string} [workspaceId] - ID del workspace (opzionale)
   * @param {string} [pageId] - ID della pagina (opzionale)
   * @returns {Promise<{accessToken: string, expiration: string, reportId: string, workspaceId: string, pageId: string, embedUrl: string, directUrl: string, tokenType: string, appOwnsData: boolean}>}
   */
  getReportEmbedInfo: async (reportId, workspaceId = null, pageId = null) => {
    try {
      // Aggiungi timestamp per evitare problemi di cache
      const timestamp = new Date().getTime();

      // Costruisci URL con parametri - Nota: l'endpoint è cambiato da getembedtoken a getembedinfo
      let url = `${getApiUrl()}/powerbi/getembedinfo?reportId=${reportId}&timestamp=${timestamp}`;
      if (workspaceId) {
        url += `&workspaceId=${workspaceId}`;
      }
      if (pageId) {
        url += `&pageId=${pageId}`;
      }

      // Effettua la chiamata HTTP con intestazioni anti-cache
      const response = await axios.get(url, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        // Timeout aumentato per gestire latenza
        timeout: 60000,
      });

      // Estrai e verifica dati
      const data = response.data;

      // Verifica token
      if (!data.accessToken) {
        console.error("[PowerBI] Token mancante nella risposta");
        throw new Error("Token di accesso mancante nella risposta API");
      }

      // Verifica embedUrl
      if (!data.embedUrl) {
        console.error("[PowerBI] embedUrl mancante nella risposta");
        throw new Error("L'URL di embedding manca nella risposta API");
      }

      // Verifica che sia in modalità App-Owns-Data
      if (!data.appOwnsData) {
        console.warn("[PowerBI] La risposta non indica modalità App-Owns-Data");
      }

      return data;
    } catch (error) {
      console.error(
        "[PowerBI] Errore nell'ottenere le informazioni di embedding:",
        error
      );

      // Gestione dettagliata errori
      if (error.response) {
        console.error("[PowerBI] Dettagli errore:", error.response.data);
        throw new Error(
          `Errore nell'ottenere le info di embedding: ${
            error.response.status
          } ${error.response.data?.error || error.response.statusText} ${
            error.response.data?.details || ""
          }`
        );
      } else if (error.request) {
        throw new Error(
          "Nessuna risposta dal server per la richiesta delle info di embedding"
        );
      } else {
        throw new Error(`Errore nella richiesta: ${error.message}`);
      }
    }
  },

  /**
   * Aggiorna il dataset di Power BI utilizzando il backend (per utilizzare le credenziali specifiche)
   * @param {string} workspaceId - ID del workspace
   * @param {string} datasetId - ID del dataset da aggiornare
   * @returns {Promise<any>}
   */
  refreshDatasetViaBackend: async (workspaceId, datasetId) => {
    try {

      // Aggiungi timestamp per evitare la cache
      const timestamp = new Date().getTime();

      const response = await axios.post(
        `${getApiUrl()}/powerbi/refreshdataset?timestamp=${timestamp}`,
        {
          workspaceId: workspaceId,
          datasetId: datasetId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
          },
          // Timeout aumentato per gestire latenza
          timeout: 60000,
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          `Errore nell'aggiornare il dataset: ${error.response.status} ${
            error.response.data?.error || error.response.statusText
          } ${error.response.data?.details || ""}`
        );
      } else if (error.request) {
        throw new Error(
          "Nessuna risposta dal server per la richiesta di aggiornamento dataset"
        );
      } else {
        throw new Error(`Errore nella richiesta: ${error.message}`);
      }
    }
  },
};

export default PowerBIService;
